import React from "react";
import { IForm } from "../../../../types/form.type";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { useAlertContext } from "../../../../contexts/alert/AlertProvider";
import { Controller, useForm } from "react-hook-form";
import { asyncPostData, asyncPutData } from "../../../../utils/httpRequests";
import ModalForm from "../../modal/ModalForm";
import { Stack, Typography } from "@mui/material";
import TextInput from "../../input/TextInput";
import { generateRandomString, numeralCustom } from "../../../../utils/helpers";
import NumberInput from "../../input/NumberInput";
import SelectApiInput from "../../input/SelectApiInput";

const schema = yup.object({
  ten: yup.string().required("Vui lòng nhập tên"),
  route: yup
    .object()
    .typeError("Vui lòng chọn tuyến")
    .required("Vui lòng chọn tuyến"),
  nha_xe: yup
    .object()
    .typeError("Vui lòng chọn nhà xe")
    .required("Vui lòng chọn nhà xe"),
  loai_xe: yup
    .object()
    .typeError("Vui lòng chọn loại xe")
    .required("Vui lòng chọn loại xe"),
  gia_ve: yup.string().required("Vui lòng nhập giá vé"),
});

function FormHangVeXe({
  defaultValues,
  isEdit,
  apiCode,
  open,
  onClose,
  setLoad,
}: IForm) {
  const app = useSelector((state: any) => state.app.data);
  const token = useSelector((state: any) => state.auth.token);
  const { showAlert } = useAlertContext();
  const defaultData: { [key: string]: any } = {
    ma: "",
    ten: "",
    route: null,
    nha_xe: null,
    loai_xe: null,
    gia_ve: 0,
    hoahong: 0,
  };
  const originData = defaultValues
    ? {
        ...defaultValues,
        route: defaultValues?.route
          ? {
              _id: defaultValues?.route || "",
              name: defaultValues?.ten_tuyen || "",
            }
          : null,
        nha_xe: defaultValues?.nhaxe
          ? {
              ma_dt: defaultValues?.nhaxe || "",
              ten_dt: defaultValues?.ten_nhaxe || "",
            }
          : null,
        loai_xe: defaultValues?.loaixe
          ? {
              _id: defaultValues?.loaixe || "",
              name: defaultValues?.ten_loai_xe || "",
            }
          : null,
      }
    : defaultData;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    mode: "onSubmit",
    defaultValues: originData,
    resolver: yupResolver(schema),
  });

  const handleReset = () => {
    reset(originData);
  };

  const generateDataPost = async (values: typeof defaultData) => {
    const { ma, route, nha_xe, loai_xe, ...fields } = values;
    return {
      ...fields,
      ma: ma || generateRandomString(8),
      route: route?._id || "",
      nhaxe: nha_xe?.ma_dt || "",
      loaixe: loai_xe?._id,
    };
  };

  const handleSave = async (values: typeof defaultData) => {
    const dataPost: { [key: string]: any } = await generateDataPost(values);
    let resp = null;
    if (isEdit) {
      resp = await asyncPutData({
        id: dataPost._id,
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    } else {
      resp = await asyncPostData({
        apiCode,
        token,
        idApp: app?._id,
        data: dataPost,
      });
    }
    if (resp.status !== 200) {
      showAlert({
        type: "error",
        message:
          resp?.data?.error || resp?.data?.message || "Lỗi khi lưu đối tượng",
      });
    } else {
      onClose();
      setLoad?.((prev: number) => prev + 1);
    }
  };

  const onSubmit = handleSubmit(handleSave);

  return (
    <ModalForm
      formWidth="500px"
      formTitle="hạng vé xe"
      loading={isSubmitting}
      open={open}
      isEdit={isEdit}
      onClose={onClose}
      onSubmit={onSubmit}
      onReset={handleReset}
    >
      <Stack gap={2}>
        <TextInput
          readOnly={isEdit}
          label="Mã"
          labelWidth="30%"
          placeholder="Nhập hoặc tạo tự động"
          name="ma"
          register={register}
        />
        <TextInput
          label="Tên"
          required
          labelWidth="30%"
          placeholder="Nhập tên hạng vé"
          name="ten"
          register={register}
          errorMessage={errors?.ten?.message as string}
        />
        <Controller
          name="route"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                label="Tuyến đường"
                required
                labelWidth="30%"
                placeholder="Chọn tuyến đường"
                apiCode="route"
                getOptionLabel={(option) => option.name}
                checkEqual={(value, option) => value?._id === option?._id}
                searchFields={["name"]}
                value={value}
                onChange={onChange}
                errorMessage={errors?.route?.message as string}
              />
            );
          }}
        />
        <Controller
          name="nha_xe"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                label="Nhà xe"
                required
                labelWidth="30%"
                placeholder="Chọn nhà xe"
                apiCode="dmnhaxe"
                getOptionLabel={(option) => option.ten_dt}
                checkEqual={(value, option) => value?.ma_dt === option?.ma_dt}
                searchFields={["ma_dt", "ten_dt"]}
                value={value}
                onChange={onChange}
                errorMessage={errors?.nha_xe?.message as string}
              />
            );
          }}
        />
        <Controller
          name="loai_xe"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <SelectApiInput
                label="Loại xe"
                required
                labelWidth="30%"
                placeholder="Chọn loại xe"
                apiCode="dmloaixe"
                getOptionLabel={(option) => option.name}
                checkEqual={(value, option) => value?._id === option?._id}
                searchFields={["name"]}
                value={value}
                onChange={onChange}
                errorMessage={errors?.loai_xe?.message as string}
              />
            );
          }}
        />
        <Controller
          name="gia_ve"
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <TextInput
                label="Giá vé"
                labelWidth="30%"
                value={numeralCustom(value).format()}
                onChange={(e) => {
                  const val = numeralCustom(e.target.value).value();
                  onChange(val);
                }}
                errorMessage={errors?.gia_ve?.message as string}
              />
            );
          }}
        />
        <NumberInput
          label="Hoa hồng"
          labelWidth="30%"
          register={register}
          name="hoahong"
          endAdornment={<Typography sx={{ px: 1 }}>%</Typography>}
        />
      </Stack>
    </ModalForm>
  );
}

export default FormHangVeXe;
